import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { readFileAsDataUrl, readFileInputChange } from "../lib/image-input";
import { runRefineUpload } from "../lib/refine-upload-actions";
import { useImageDropZone } from "./useImageDropZone";
import { useLatestPick } from "./useLatestPick";

export interface RefineUploadState {
  previewUrl: string | null;
  hasFile: boolean;
  isUploading: boolean;
  error: string | null;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  dropZoneProps: ReturnType<typeof useImageDropZone>;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemove: () => void;
  handleRefine: () => void;
}

export function useRefineUpload(
  projectId: string | undefined,
): RefineUploadState {
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<{
    file: File;
    previewUrl: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Exactly one base image: the newest pick replaces the current one.
  const latestPick = useLatestPick();
  const readImage = (selected: File) => {
    const isLatest = latestPick.begin();
    // Clear the old image so Refine stays disabled until the new one is ready.
    setSelection(null);
    readFileAsDataUrl(selected, (previewUrl, file) => {
      if (isLatest()) setSelection({ file, previewUrl });
    });
  };

  const dropZoneProps = useImageDropZone({
    onFile: readImage,
    disabled: isUploading,
  });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    readFileInputChange(e, readImage);

  const handleRemove = () => {
    latestPick.cancel();
    setSelection(null);
  };

  const handleRefine = () => {
    runRefineUpload({
      file: selection?.file ?? null,
      projectId,
      navigate,
      queryClient,
      router,
      setIsUploading,
      setError,
    });
  };

  return {
    previewUrl: selection?.previewUrl ?? null,
    hasFile: selection !== null,
    isUploading,
    error,
    fileInputRef,
    dropZoneProps,
    handleFileInputChange,
    handleRemove,
    handleRefine,
  };
}
